import React, { useState } from "react";
import { X, AlertTriangle, Trash2 } from "lucide-react";

export default function DeleteTableModal({
  isOpen,
  onClose,
  onConfirm,
  table,
}) {
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (confirmText !== table.table_name) return;

    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setConfirmText("");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6" />
              <h2 className="text-xl font-bold">테이블 삭제</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <Trash2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900 mb-2">
                  이 작업은 되돌릴 수 없습니다!
                </p>
                <p className="text-sm text-red-700">
                  테이블을 삭제하면 다음 항목이 모두 영구적으로 삭제됩니다:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-red-600">
                  <li>• {table.row_count}개의 데이터 행</li>
                  <li>• {table.column_count}개의 컬럼 정의</li>
                  <li>• 모든 집계 설정</li>
                  <li>• 모든 공유 권한</li>
                  <li>• 모든 감사 로그</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-700 mb-2">
                삭제를 확인하려면 테이블 이름{" "}
                <span className="font-mono font-bold text-red-600">
                  {table.table_name}
                </span>
                을(를) 입력하세요:
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="테이블 이름 입력"
                disabled={submitting}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              취소
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting || confirmText !== table.table_name}
            >
              {submitting ? "삭제 중..." : "영구 삭제"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
